import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ExternalLink, Bell, Loader2 } from 'lucide-react';
import { NotificationItem, resolvePresentation } from '../../composables/notificationAPI';
import { getArticleById } from '../../composables/fetchAPI';

const CLOSE_ANIMATION_MS = 180;

// item.body ที่ backend ส่งมาให้ feed เป็นแค่ preview ที่ตัดสั้นไว้ (ดู field bodyTruncated ใน
// NotificationItem) ไม่ใช่ bug ฝั่ง frontend — เนื้อหาเต็มของ ARTICLE_PUBLISHED อยู่ที่ตัวบทความจริง
// (deepLink รูปแบบ /article/:id) เลยต้องดึงมาเองอีกทีตอนเปิด popup ถ้าอยากให้เห็นครบ ไม่ใช่แค่โชว์
// item.body ซ้ำ (ซึ่งจะยังมี "..." ติดมาเหมือนเดิมเพราะข้อมูลต้นทางที่ส่งมาแค่นี้จริง ๆ)
function extractArticleId(item: NotificationItem): string | null {
  if (item.category !== 'ARTICLE' || !item.deepLink) return null;
  const match = item.deepLink.match(/\/article\/([^/?#]+)/);
  return match ? match[1] : null;
}

// ตัด inline background/color ออกจาก HTML บทความ (เหมือนที่ ArticleDetailPage.tsx ทำ) เพื่อให้สี
// ตัวอักษร/พื้นหลังตาม theme ของแอป ไม่ใช่สีที่ติดมาจากตอนเขียนบทความ
function sanitizeArticleContent(html: string): string {
  if (!html) return '';
  return html
    .replace(/background-color\s*:[^;"]*;?/gi, '')
    .replace(/background\s*:[^;"]*;?/gi, '')
    .replace(/color\s*:[^;"]*;?/gi, '');
}

// Popup แสดงเนื้อหาแจ้งเตือนแบบเต็ม ๆ ใช้ตอนตัวการ์ด/แถวโชว์แค่บางส่วน (title truncate,
// body line-clamp) แล้วผู้ใช้กด "ดูรายละเอียด" — ดีไซน์ยึดตาม popup รายละเอียดหุ้นซื้อ-ขายใน
// RealtimePageV2.tsx (fixed + backdrop, ปิดด้วยปุ่ม X หรือกดพื้นหลังมืด, เนื้อหา scroll ได้เอง)
//
// ใช้ createPortal ไปที่ document.body เพราะจุดที่เรียกใช้ (NotificationRow ในกล่อง dropdown ของ
// NotificationPanel) อยู่ใต้ motion.div ที่มี transform (scale/translate จาก framer-motion) — CSS
// สร้าง containing block ใหม่ให้ position:fixed ทันทีที่ ancestor มี transform ทำให้ popup ไม่ได้จัด
// กึ่งกลางจอจริง ๆ แต่ไปอิงตำแหน่ง/ขนาดของ dropdown แทน ต้อง portal ออกไปนอก DOM tree นั้นเลย
const NotificationDetailModal: React.FC<{
  item: NotificationItem;
  onClose: () => void;
  onOpen?: (url: string) => void;
}> = ({ item, onClose, onOpen }) => {
  const presentation = resolvePresentation(item);
  const ctaUrl = item.primaryAction?.url || item.deepLink;
  const articleId = extractArticleId(item);

  const [articleContent, setArticleContent] = useState<string | null>(null);
  const [loadingArticle, setLoadingArticle] = useState(false);
  const [articleLoadFailed, setArticleLoadFailed] = useState(false);

  useEffect(() => {
    if (!articleId) return;
    let cancelled = false;
    setLoadingArticle(true);
    getArticleById(articleId)
      .then((data) => {
        if (cancelled) return;
        setArticleContent(data?.content || null);
      })
      .catch(() => {
        if (!cancelled) setArticleLoadFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoadingArticle(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articleId]);

  // entered คุมสถานะ animation: mount มาด้วย false (ซ่อน+เลื่อนขึ้นนิดหน่อย) แล้วสลับเป็น true
  // เฟรมถัดไปเพื่อ trigger transition (fade + เลื่อนลงมาเข้าที่) ส่วนตอนปิดจะสลับกลับเป็น false ก่อน
  // แล้วค่อยเรียก onClose จริงหลัง transition จบ กันโผล่/หายวับทันที
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  const requestClose = () => {
    setEntered(false);
    setTimeout(onClose, CLOSE_ANIMATION_MS);
  };

  const handleOpenCta = () => {
    if (onOpen) onOpen(ctaUrl!); else window.open(ctaUrl!, '_blank');
    requestClose();
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-200 ease-out"
        style={{ opacity: entered ? 1 : 0 }}
        onClick={requestClose}
      />
      <div
        className="relative w-full max-w-md max-h-[80vh] rounded-2xl shadow-2xl border flex flex-col transition-all duration-200 ease-out"
        style={{
          backgroundColor: 'var(--component-bg-color)',
          color: 'var(--text-color)',
          borderColor: 'var(--border-item-dark, transparent)',
          opacity: entered ? 1 : 0,
          transform: entered ? 'translateY(0) scale(1)' : 'translateY(-16px) scale(0.97)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 p-4 border-b" style={{ borderColor: 'var(--bg-item)' }}>
          <div
            className="w-12 h-12 rounded-lg overflow-hidden shrink-0 flex items-center justify-center text-xl"
            style={{ backgroundColor: 'var(--bg-navbar, #e5e7eb)' }}
          >
            {item.image ? (
              <img src={item.image} alt="" className="w-full h-full object-cover" />
            ) : presentation.icon ? (
              presentation.icon
            ) : (
              <span style={{ color: 'var(--secondary-text-color)' }}><Bell size={18} /></span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <span
              className="inline-block text-[10px] font-bold px-2 py-0.5 rounded-full mb-1"
              style={{ backgroundColor: 'var(--bg-navbar, #e5e7eb)', color: 'var(--secondary-text-color)' }}
            >
              {presentation.icon ? `${presentation.icon} ${presentation.label}` : presentation.label}
            </span>
            <div className="text-sm font-bold break-words">{item.title}</div>
          </div>
          <button
            type="button"
            onClick={requestClose}
            className="shrink-0 w-7 h-7 flex items-center justify-center rounded-full opacity-70 hover:opacity-100 transition-opacity"
            style={{ backgroundColor: 'var(--bg-item)' }}
            title="ปิด"
          >
            <X size={14} />
          </button>
        </div>

        <div className="p-4 overflow-y-auto">
          {loadingArticle ? (
            <div className="flex items-center gap-2 text-sm opacity-70 py-6 justify-center">
              <Loader2 size={16} className="animate-spin" /> กำลังโหลดเนื้อหาเต็ม...
            </div>
          ) : articleContent ? (
            <div
              className="text-sm break-words prose prose-invert prose-sm max-w-none"
              style={{ color: 'var(--text-color)' }}
              dangerouslySetInnerHTML={{ __html: sanitizeArticleContent(articleContent) }}
            />
          ) : (
            <>
              <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{item.body}</p>
              {(articleId && articleLoadFailed) && (
                <p className="text-xs opacity-60 mt-2">โหลดเนื้อหาเต็มไม่สำเร็จ — กด "{presentation.cta}" เพื่อไปอ่านที่หน้าบทความแทน</p>
              )}
            </>
          )}
        </div>

        <div className="flex items-center gap-2 p-4 pt-0 flex-wrap">
          {ctaUrl && (
            <button
              type="button"
              onClick={handleOpenCta}
              className="inline-flex items-center gap-1 text-[12px] font-semibold px-3 py-1.5 rounded-full border"
              style={{ borderColor: 'var(--bg-navbar, #333)', color: 'var(--text-color)' }}
            >
              {presentation.cta} <ExternalLink size={12} />
            </button>
          )}
          {item.acknowledgement?.required && (
            <span
              className="inline-flex items-center text-[12px] font-semibold px-3 py-1.5 rounded-full"
              style={{ backgroundColor: 'var(--green-text-color)', color: '#fff' }}
            >
              {item.acknowledgement.label || 'รับทราบ'}
            </span>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default NotificationDetailModal;
